import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertModal, ConfirmModal } from './AlertModal';
import GroupStageView from './GroupStageView'; // old GSL / mini-elim group stages only
import EliminationPage from './bracket/EliminationPage';
import StagePage from './bracket/StagePage';
import CreateBracketModal from './bracket/CreateBracketModal';
import { useTranslation } from 'react-i18next';
import './PublicMatches.css';

// embedded: shown as the Brackets tab of the season page (no "Back to Seasons" / season title)
// bracketId (embedded): the bracket in the address (/admin/seasons/:id/brackets/:bracketId) - every
// bracket of the season has its own link; opening / closing one changes the address.
function Brackets({ embedded = false, seasonId: seasonIdProp, bracketId: bracketIdProp } = {}) {
  const { t } = useTranslation();
  const params = useParams();
  const seasonId = seasonIdProp != null ? String(seasonIdProp) : params.seasonId;
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [brackets, setBrackets] = useState([]);
  const [teams, setTeams] = useState([]);
  const [servers, setServers] = useState([]);
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCreate, setNewCreate] = useState(false);   // create dialog (bracket/CreateBracketModal, every format)
  const [selectedBracket, setSelectedBracket] = useState(null);
  const [bracketData, setBracketData] = useState(null); // { bracket, matches, teams }
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const mountedRef = useRef(true);
  const selectedBracketRef = useRef(selectedBracket);

  // Keep ref in sync with state
  useEffect(() => { selectedBracketRef.current = selectedBracket; }, [selectedBracket]);

  // Stable loadBracket function for WS callback
  const loadBracketSilent = useCallback(async (id) => {
    try {
      const res = await axios.get(`/api/brackets/${id}`);
      if (mountedRef.current) setBracketData(res.data);
    } catch (_) {}
  }, []);

  // WebSocket connection for live bracket updates
  useEffect(() => {
    mountedRef.current = true;

    function connectWs() {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;
      const port = (host === 'localhost' || host === '127.0.0.1') ? ':5000' : (window.location.port ? `:${window.location.port}` : '');
      const token = localStorage.getItem('token');
      const wsUrl = `${protocol}//${host}${port}/ws${token ? `?token=${token}` : ''}`;

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'bracket_update' && selectedBracketRef.current) {
              // Reload bracket data if we're viewing any bracket (bracket_id may differ for score updates)
              loadBracketSilent(selectedBracketRef.current);
            }
            // Also react to score_update and match_finished for bracket-linked matches
            if ((message.type === 'score_update' || message.type === 'match_finished' || message.type === 'match_status_change') && selectedBracketRef.current) {
              loadBracketSilent(selectedBracketRef.current);
            }
          } catch (_) {}
        };

        ws.onclose = () => {
          if (mountedRef.current) {
            reconnectRef.current = setTimeout(connectWs, 3000);
          }
        };

        ws.onerror = () => {
          try { ws.close(); } catch (_) {}
        };
      } catch (_) {}
    }

    connectWs();

    return () => {
      mountedRef.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) {}
      }
    };
  }, [loadBracketSilent]);

  useEffect(() => { fetchAll(); }, [seasonId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [seasRes, brRes, teamsRes, serversRes, mapsRes] = await Promise.all([
        axios.get('/api/seasons'),
        axios.get(`/api/brackets?season_id=${seasonId}`),
        axios.get('/api/teams?limit=500'),
        axios.get('/api/servers?limit=500'),
        axios.get('/api/maps')
      ]);
      const s = (seasRes.data || []).find(s => s.id === parseInt(seasonId));
      setSeason(s || null);
      setBrackets(brRes.data || []);
      setTeams(teamsRes.data?.data || teamsRes.data || []);
      setServers(serversRes.data?.data || serversRes.data || []);
      setMaps(mapsRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadBracket = async (id) => {
    try {
      const res = await axios.get(`/api/brackets/${id}`);
      setBracketData(res.data);
      setSelectedBracket(id);
    } catch (err) {
      setAlertModal({ show: true, message: 'Failed to load bracket', type: 'error' });
      if (embedded && bracketIdProp) navigate(`/admin/seasons/${seasonId}/brackets`, { replace: true }); // wrong / deleted link → the list
    }
  };

  // Open / close a bracket: in the season page through its address, else in place
  const openBracket = (id) => {
    if (embedded) navigate(`/admin/seasons/${seasonId}/brackets/${id}`);
    else loadBracket(id);
  };
  const closeBracket = () => {
    if (embedded) navigate(`/admin/seasons/${seasonId}/brackets`);
    else { setSelectedBracket(null); setBracketData(null); fetchAll(); }
  };
  // The address changed (open, back / forward, a shared link, refresh) → show that bracket or the list
  useEffect(() => {
    if (!embedded) return;
    const id = bracketIdProp ? parseInt(bracketIdProp, 10) : null;
    if (id) { if (id !== selectedBracketRef.current) loadBracket(id); }
    else if (selectedBracketRef.current) { setSelectedBracket(null); setBracketData(null); fetchAll(); }
  }, [embedded, bracketIdProp]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteBracket = (id) => {
    setConfirmModal({
      show: true,
      message: t('brackets.deleteConfirm', 'Delete this bracket and all its matches? This cannot be undone.'),
      onConfirm: async () => {
        try {
          await axios.delete(`/api/brackets/${id}`);
          setAlertModal({ show: true, message: 'Bracket deleted', type: 'success' });
          if (selectedBracket === id) closeBracket();
          else fetchAll();
        } catch (err) {
          setAlertModal({ show: true, message: 'Failed to delete', type: 'error' });
        }
        setConfirmModal({ show: false });
      }
    });
  };

  const handleSetWinner = async (bracketId, matchId, winnerId) => {
    try {
      const res = await axios.put(`/api/brackets/${bracketId}/matches/${matchId}/winner`, { winner_id: winnerId });
      setBracketData(prev => ({ ...prev, matches: res.data.matches }));
    } catch (err) {
      setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to set winner', type: 'error' });
    }
  };

  const handleResetMatch = async (bracketId, matchId) => {
    setConfirmModal({
      show: true,
      message: 'Reset this match and all downstream results?',
      onConfirm: async () => {
        try {
          const res = await axios.put(`/api/brackets/${bracketId}/matches/${matchId}/reset`);
          setBracketData(prev => ({ ...prev, matches: res.data.matches }));
        } catch (err) {
          setAlertModal({ show: true, message: 'Failed to reset', type: 'error' });
        }
        setConfirmModal({ show: false });
      }
    });
  };

  const handleRandomize = async (bracketId) => {
    try {
      const res = await axios.post(`/api/brackets/${bracketId}/randomize`);
      setBracketData(prev => ({ ...prev, bracket: res.data.bracket, matches: res.data.matches }));
      setAlertModal({ show: true, message: 'Seeding randomized!', type: 'success' });
    } catch (err) {
      setAlertModal({ show: true, message: 'Failed to randomize', type: 'error' });
    }
  };

  const handleStartTournament = async (bracketId) => {
    setConfirmModal({
      show: true,
      message: 'Start the tournament? This will lock the bracket seeding and disable randomization.',
      onConfirm: async () => {
        try {
          await axios.put(`/api/brackets/${bracketId}`, { status: 'active' });
          setBracketData(prev => ({ ...prev, bracket: { ...prev.bracket, status: 'active' } }));
          setAlertModal({ show: true, message: 'Tournament started!', type: 'success' });
        } catch (err) {
          setAlertModal({ show: true, message: 'Failed to start tournament', type: 'error' });
        }
        setConfirmModal({ show: false });
      }
    });
  };

  const handleSwapTeams = async (bracketId, round1, position1, slot1, round2, position2, slot2, side1, side2) => {
    try {
      const res = await axios.put(`/api/brackets/${bracketId}/swap`, { round1, position1, slot1, round2, position2, slot2, side1, side2 });
      setBracketData(prev => ({ ...prev, matches: res.data.matches }));
    } catch (err) {
      setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to swap teams', type: 'error' });
    }
  };

  const handleFinishBracket = async (bracketId) => {
    setConfirmModal({
      show: true,
      message: 'Mark this bracket as finished? This indicates the tournament has concluded.',
      onConfirm: async () => {
        try {
          await axios.put(`/api/brackets/${bracketId}`, { status: 'finished' });
          setBracketData(prev => ({ ...prev, bracket: { ...prev.bracket, status: 'finished' } }));
          setAlertModal({ show: true, message: 'Bracket marked as finished!', type: 'success' });
        } catch (err) {
          setAlertModal({ show: true, message: 'Failed to finish bracket', type: 'error' });
        }
        setConfirmModal({ show: false });
      }
    });
  };

  const handleCancelBracket = async (bracketId) => {
    setConfirmModal({
      show: true,
      message: 'Cancel this bracket? This marks the tournament as cancelled.',
      onConfirm: async () => {
        try {
          await axios.put(`/api/brackets/${bracketId}`, { status: 'cancelled' });
          setBracketData(prev => ({ ...prev, bracket: { ...prev.bracket, status: 'cancelled' } }));
          setAlertModal({ show: true, message: 'Bracket cancelled', type: 'success' });
        } catch (err) {
          setAlertModal({ show: true, message: 'Failed to cancel bracket', type: 'error' });
        }
        setConfirmModal({ show: false });
      }
    });
  };

  const handleResetBracket = async (bracketId) => {
    setConfirmModal({
      show: true,
      message: 'Reset entire bracket? This will clear ALL match results and return the bracket to draft mode. This cannot be undone.',
      onConfirm: async () => {
        try {
          const res = await axios.put(`/api/brackets/${bracketId}/reset`);
          setBracketData(prev => ({ ...prev, bracket: res.data.bracket, matches: res.data.matches }));
          setAlertModal({ show: true, message: 'Bracket reset to draft!', type: 'success' });
        } catch (err) {
          setAlertModal({ show: true, message: 'Failed to reset bracket', type: 'error' });
        }
        setConfirmModal({ show: false });
      }
    });
  };

  const handleScheduleMatch = async (bracketId, matchId, serverId, map) => {
    try {
      const res = await axios.post(`/api/brackets/${bracketId}/matches/${matchId}/schedule`, {
        server_id: serverId,
        map: map
      });
      setAlertModal({ show: true, message: `Match #${res.data.match.id} created! Open the match page and click "Send to Server" to start.`, type: 'success' });
      loadBracket(bracketId);
    } catch (err) {
      setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to schedule', type: 'error' });
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', color: 'var(--text-secondary, #8b8b9e)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div className="spinner" style={{ width: 20, height: 20, border: '2px solid var(--border-subtle, rgba(255,255,255,0.06))', borderTopColor: 'var(--accent-blue, #3b82f6)', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
          Loading...
        </div>
      </div>
    );
  }

  // Season not found or not owned by current admin
  if (!season) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.2rem', color: 'var(--accent-red, #ef4444)', marginBottom: '0.5rem', fontFamily: 'var(--font-display)' }}>{t('brackets.seasonNotFound', 'Season Not Found')}</div>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #8b8b9e)' }}>
          {t('brackets.seasonNotFoundHint', "This season doesn't exist or doesn't belong to you.")}
        </div>
        <button onClick={() => navigate('/admin/seasons')} style={{ marginTop: '1rem', padding: '0.5rem 1.5rem', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', color: 'var(--accent-blue, #3b82f6)', borderRadius: 'var(--radius-md, 8px)', cursor: 'pointer', fontWeight: 600 }}>
          ← {t('brackets.backToSeasons', 'Back to Seasons')}
        </button>
      </div>
    );
  }

  // Swiss + round-robin groups: the rebuilt stage page (bracket/StagePage). Old GSL groups keep the old view.
  // engine groups (round robin or GSL 'dual') → StagePage; old GSL / mini-elim groups keep the old view
  const rrGroups = (d) => d.bracket?.bracket_type === 'group_stage' && Array.isArray(d.groups) && d.groups.length > 0 && d.groups.every(g => g.group_format === 'round_robin' || g.group_format === 'dual');
  if (selectedBracket && bracketData && (bracketData.bracket?.bracket_type === 'swiss' || rrGroups(bracketData))) {
    return (
      <>
        <ConfirmModal show={confirmModal.show} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onClose={() => setConfirmModal({ show: false })} />
        <StagePage bracketData={bracketData} servers={servers} allTeams={teams}
          onBack={closeBracket}
          onReload={() => loadBracketSilent(selectedBracket)}
          onDelete={handleDeleteBracket} />
      </>
    );
  }

  // Single + double elimination: the rebuilt page (bracket/EliminationPage)
  if (selectedBracket && bracketData && ['single_elimination', 'double_elimination'].includes(bracketData.bracket?.bracket_type || 'single_elimination')) {
    return (
      <>
        <ConfirmModal show={confirmModal.show} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onClose={() => setConfirmModal({ show: false })} />
        <EliminationPage bracketData={bracketData} servers={servers} allTeams={teams}
          onBack={closeBracket}
          onReload={() => loadBracketSilent(selectedBracket)}
          onDelete={handleDeleteBracket} />
      </>
    );
  }

  // If we have a selected bracket, show the bracket view
  if (selectedBracket && bracketData) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <AlertModal show={alertModal.show} message={alertModal.message} type={alertModal.type} onClose={() => setAlertModal({ ...alertModal, show: false })} />
        <ConfirmModal show={confirmModal.show} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onClose={() => setConfirmModal({ show: false })} />

        {/* Header bar */}
        <div style={{
          padding: '0.75rem 1.5rem',
          borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
          display: 'flex', alignItems: 'center', gap: '1rem',
          background: 'var(--bg-card, #111118)',
          flexShrink: 0,
          position: 'relative',
        }}>
          <button onClick={closeBracket} style={{
            background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)',
            color: 'var(--accent-blue, #3b82f6)', padding: '0.4rem 0.8rem', borderRadius: 'var(--radius-md, 8px)', cursor: 'pointer', fontSize: '0.8rem',
            fontWeight: 600, transition: 'all 0.15s',
          }}>
            ← {t('common.back', 'Back')}
          </button>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
            <h2 style={{
              margin: 0, fontSize: '1.15rem', fontWeight: 800, fontFamily: 'var(--font-display)',
              color: 'var(--text-primary, #e8e8ed)',
            }}>
              {bracketData.bracket?.name || t('brackets.bracket', 'Bracket')}
            </h2>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #4a4a5a)', fontWeight: 500 }}>
              {season?.name}
            </span>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            {bracketData.bracket?.status === 'active' && (
              <span style={{
                fontSize: '0.75rem', fontWeight: 800, color: 'var(--accent-green, #22c55e)',
                background: 'rgba(34,197,94,0.08)', padding: '4px 12px', borderRadius: 'var(--radius-sm, 6px)',
                border: '1px solid rgba(34,197,94,0.15)', letterSpacing: '0.06em', textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', gap: '4px',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-green, #22c55e)', animation: 'bracketPulse 1.5s ease-in-out infinite' }} />
                {t('matchStatus.live', 'Live')}
              </span>
            )}
            {bracketData.bracket?.status === 'completed' && (
              <span style={{
                fontSize: '0.75rem', fontWeight: 800, color: 'var(--accent-amber, #f59e0b)',
                background: 'rgba(245,158,11,0.08)', padding: '4px 12px', borderRadius: 'var(--radius-sm, 6px)',
                border: '1px solid rgba(245,158,11,0.15)', letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                🏆 {t('bracketStatus.completed', 'Completed')}
              </span>
            )}
            {bracketData.bracket?.status === 'finished' && (
              <span style={{
                fontSize: '0.75rem', fontWeight: 800, color: '#a78bfa',
                background: 'rgba(167,139,250,0.08)', padding: '4px 12px', borderRadius: 'var(--radius-sm, 6px)',
                border: '1px solid rgba(167,139,250,0.15)', letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                ✅ {t('matchStatus.finished', 'Finished')}
              </span>
            )}
            {bracketData.bracket?.status === 'cancelled' && (
              <span style={{
                fontSize: '0.75rem', fontWeight: 800, color: 'var(--accent-red, #ef4444)',
                background: 'rgba(239,68,68,0.08)', padding: '4px 12px', borderRadius: 'var(--radius-sm, 6px)',
                border: '1px solid rgba(239,68,68,0.15)', letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                ✕ {t('matchStatus.cancelled', 'Cancelled')}
              </span>
            )}
            <button
              onClick={() => handleRandomize(selectedBracket)}
              disabled={bracketData.bracket?.status !== 'draft'}
              style={{
                background: bracketData.bracket?.status === 'draft' ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${bracketData.bracket?.status === 'draft' ? 'rgba(59,130,246,0.15)' : 'var(--border-subtle, rgba(255,255,255,0.06))'}`,
                color: bracketData.bracket?.status === 'draft' ? 'var(--accent-blue, #3b82f6)' : 'var(--text-muted, #4a4a5a)',
                padding: '0.4rem 0.8rem', borderRadius: 'var(--radius-sm, 6px)',
                cursor: bracketData.bracket?.status === 'draft' ? 'pointer' : 'not-allowed',
                fontSize: '0.75rem', fontWeight: 600,
                opacity: bracketData.bracket?.status === 'draft' ? 1 : 0.4,
                transition: 'all 0.15s',
              }}
            >
              🎲 {t('brackets.randomize', 'Randomize')}
            </button>
            {bracketData.bracket?.status === 'draft' && (
              <button onClick={() => handleStartTournament(selectedBracket)} style={{
                background: 'var(--accent-green, #22c55e)',
                border: 'none', color: '#fff',
                padding: '0.45rem 1.2rem', borderRadius: 'var(--radius-sm, 6px)', cursor: 'pointer',
                fontSize: '0.75rem', fontWeight: 700,
                transition: 'all 0.2s',
              }}>
                {t('brackets.startTournament', 'Start Tournament')}
              </button>
            )}
            {bracketData.bracket?.status === 'active' && (
              <>
                <button onClick={() => handleFinishBracket(selectedBracket)} style={{
                  background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.15)',
                  color: '#a78bfa', padding: '0.4rem 0.8rem', borderRadius: 'var(--radius-sm, 6px)', cursor: 'pointer',
                  fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s',
                }}>
                  ✅ {t('brackets.finish', 'Finish')}
                </button>
                <button onClick={() => handleCancelBracket(selectedBracket)} style={{
                  background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)',
                  color: 'var(--accent-red, #ef4444)', padding: '0.4rem 0.8rem', borderRadius: 'var(--radius-sm, 6px)', cursor: 'pointer',
                  fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s',
                }}>
                  ✕ {t('common.cancel', 'Cancel')}
                </button>
              </>
            )}
            {bracketData.bracket?.status !== 'draft' && (
              <button onClick={() => handleResetBracket(selectedBracket)} style={{
                background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)',
                color: 'var(--accent-amber, #f59e0b)', padding: '0.4rem 0.8rem', borderRadius: 'var(--radius-sm, 6px)', cursor: 'pointer',
                fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s',
              }}>
                🔄 {t('brackets.resetBracket', 'Reset Bracket')}
              </button>
            )}
          </div>
        </div>

        {/* Old GSL / mini-elim group stage (every other format has its own page above) */}
        {bracketData.bracket?.bracket_type === 'group_stage' && (
          <GroupStageView
            bracketData={bracketData}
            bracketId={selectedBracket}
            bracketStatus={bracketData.bracket?.status || 'draft'}
            onSetWinner={(matchId, winnerId) => handleSetWinner(selectedBracket, matchId, winnerId)}
            onResetMatch={(matchId) => handleResetMatch(selectedBracket, matchId)}
            onScheduleMatch={(matchId, serverId, map) => handleScheduleMatch(selectedBracket, matchId, serverId, map)}
            onSwapTeams={(r1, pos1, slot1, r2, pos2, slot2, side1, side2) => handleSwapTeams(selectedBracket, r1, pos1, slot1, r2, pos2, slot2, side1, side2)}
            onUpdateGroupBo={async (groupId, bo) => {
              try {
                await axios.put(`/api/brackets/${selectedBracket}/groups/${groupId}`, { best_of: bo });
                loadBracket(selectedBracket);
              } catch (err) {
                setAlertModal({ show: true, message: 'Failed to update group format', type: 'error' });
              }
            }}
            onUpdateGroupFormat={async (groupId, format) => {
              try {
                await axios.put(`/api/brackets/${selectedBracket}/groups/${groupId}`, { group_format: format });
                loadBracket(selectedBracket);
              } catch (err) {
                setAlertModal({ show: true, message: 'Failed to update group format', type: 'error' });
              }
            }}
            onAddTeamToGroup={async (groupId, teamId) => {
              try {
                await axios.post(`/api/brackets/${selectedBracket}/groups/${groupId}/teams`, { team_id: teamId });
                loadBracket(selectedBracket);
              } catch (err) {
                setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to add team', type: 'error' });
              }
            }}
            onRemoveTeamFromGroup={async (groupId, teamId) => {
              try {
                await axios.delete(`/api/brackets/${selectedBracket}/groups/${groupId}/teams/${teamId}`);
                loadBracket(selectedBracket);
              } catch (err) {
                setAlertModal({ show: true, message: 'Failed to remove team', type: 'error' });
              }
            }}
            onSwapGroupTeams={async (groupId, teamId1, teamId2) => {
              try {
                await axios.put(`/api/brackets/${selectedBracket}/groups/${groupId}/teams/reorder`, { team_id_1: teamId1, team_id_2: teamId2 });
                loadBracket(selectedBracket);
              } catch (err) {
                setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to swap teams', type: 'error' });
              }
            }}
            onGenerateMatches={async () => {
              try {
                const res = await axios.post(`/api/brackets/${selectedBracket}/generate-group-matches`);
                setAlertModal({ show: true, message: `Generated ${res.data.matches_created} matches!`, type: 'success' });
                loadBracket(selectedBracket);
              } catch (err) {
                setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to generate matches', type: 'error' });
              }
            }}
            onAddGroup={async () => {
              try {
                await axios.post(`/api/brackets/${selectedBracket}/groups`);
                loadBracket(selectedBracket);
              } catch (err) {
                setAlertModal({ show: true, message: 'Failed to add group', type: 'error' });
              }
            }}
            onDeleteGroup={async (groupId) => {
              try {
                await axios.delete(`/api/brackets/${selectedBracket}/groups/${groupId}`);
                loadBracket(selectedBracket);
              } catch (err) {
                setAlertModal({ show: true, message: 'Failed to delete group', type: 'error' });
              }
            }}
            onUpdateMatchBo={async (matchId, bo) => {
              try {
                await axios.put(`/api/brackets/${selectedBracket}/matches/${matchId}/best-of`, { best_of: bo });
                loadBracket(selectedBracket);
              } catch (err) {
                setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to update match format', type: 'error' });
              }
            }}
            onSwapMatchTeams={async (matchId) => {
              try {
                await axios.put(`/api/brackets/${selectedBracket}/matches/${matchId}/swap-teams`);
                loadBracket(selectedBracket);
              } catch (err) {
                setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to swap teams', type: 'error' });
              }
            }}
            allTeams={teams}
            servers={servers}
          />
        )}

     </div>
   );
 }

  // Otherwise show bracket list
  return (
    <div style={{ padding: embedded ? 0 : '1.5rem' }}>
      <AlertModal show={alertModal.show} message={alertModal.message} type={alertModal.type} onClose={() => setAlertModal({ ...alertModal, show: false })} />
      <ConfirmModal show={confirmModal.show} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onClose={() => setConfirmModal({ show: false })} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: embedded ? 'flex-end' : 'space-between', marginBottom: embedded ? '0.75rem' : '1.5rem' }}>
        {!embedded && <div>
          <button onClick={() => navigate('/admin/seasons')} style={{
            background: 'none', border: 'none', color: 'var(--text-muted, #4a4a5a)', cursor: 'pointer', fontSize: '0.8rem', padding: 0, marginBottom: '0.3rem',
            display: 'flex', alignItems: 'center', gap: '4px', transition: 'color 0.15s',
          }}>
            ← {t('brackets.backToSeasons', 'Back to Seasons')}
          </button>
          <h1 style={{
            margin: 0, fontSize: '1.5rem', fontWeight: 800, fontFamily: 'var(--font-display)',
            color: 'var(--text-primary, #e8e8ed)',
          }}>
            {t('nav.brackets', 'Brackets')} - {season?.name || `Season #${seasonId}`}
          </h1>
        </div>}
        <button onClick={() => setNewCreate(true)} style={{
          background: 'var(--accent-blue, #3b82f6)',
          border: 'none', color: '#fff',
          padding: '0.6rem 1.2rem', borderRadius: 'var(--radius-md, 8px)', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem',
          transition: 'all 0.2s',
        }}>
          + {t('brackets.createBracket', 'Create Bracket')}
        </button>
      </div>

      {/* Bracket list */}
      {brackets.length === 0 ? (
        <div style={{
          padding: '4rem 2rem', textAlign: 'center',
          background: 'var(--bg-card, #111118)',
          borderRadius: 'var(--radius-lg, 12px)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🏆</div>
          <p style={{ color: 'var(--text-muted, #4a4a5a)', fontSize: '0.9rem', margin: 0 }}>{t('brackets.noBrackets', 'No brackets yet. Create one to get started.')}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {[...brackets].sort((a, b) => {
            // Active first, then by date (ascending), then by name
            if (a.status === 'active' && b.status !== 'active') return -1;
            if (b.status === 'active' && a.status !== 'active') return 1;
            if (a.scheduled_date && b.scheduled_date) return a.scheduled_date.localeCompare(b.scheduled_date);
            if (a.scheduled_date && !b.scheduled_date) return -1;
            if (!a.scheduled_date && b.scheduled_date) return 1;
            return 0;
          }).map(b => {
            const statusConfig = {
              completed: { bg: 'rgba(34,197,94,0.06)', color: 'var(--accent-green, #22c55e)', border: 'rgba(34,197,94,0.12)' },
              active: { bg: 'rgba(59,130,246,0.06)', color: 'var(--accent-blue, #3b82f6)', border: 'rgba(59,130,246,0.12)' },
              finished: { bg: 'rgba(167,139,250,0.06)', color: '#a78bfa', border: 'rgba(167,139,250,0.12)' },
              cancelled: { bg: 'rgba(239,68,68,0.06)', color: 'var(--accent-red, #ef4444)', border: 'rgba(239,68,68,0.12)' },
              draft: { bg: 'rgba(100,116,139,0.06)', color: 'var(--text-secondary, #8b8b9e)', border: 'rgba(100,116,139,0.1)' },
            };
            const sc = statusConfig[b.status] || statusConfig.draft;
            return (
              <div key={b.id} className="bracket-match-card" style={{
                padding: '1rem 1.25rem',
                background: 'var(--bg-card, #111118)',
                borderRadius: 'var(--radius-lg, 12px)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                cursor: 'pointer', transition: 'all 0.2s ease',
                position: 'relative', overflow: 'hidden',
              }} onClick={() => openBracket(b.id)}>
                {/* Subtle left accent */}
                <div style={{
                  position: 'absolute', left: 0, top: '15%', bottom: '15%', width: '2px',
                  borderRadius: '0 2px 2px 0',
                  background: sc.color,
                  opacity: 0.5,
                }} />
                <div style={{ paddingLeft: '0.5rem' }}>
                  <div style={{ fontWeight: 700, color: 'var(--text-primary, #e8e8ed)', fontSize: '1rem', letterSpacing: '0.01em' }}>{b.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #4a4a5a)', marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {b.scheduled_date && (
                      <>
                        <span style={{ color: 'var(--text-secondary, #8b8b9e)' }}>📅 {b.scheduled_date}</span>
                        <span style={{ color: 'var(--border-default, rgba(255,255,255,0.09))' }}>·</span>
                      </>
                    )}
                    <span>{t('brackets.teamsCount', '{{count}} teams', { count: b.team_count })}</span>
                    {b.playoff_config && <span style={{ color: 'var(--accent-blue, #3b82f6)', fontWeight: 600 }}>+ {t('bracket.po.title', 'Playoffs')}</span>}
                    <span style={{ color: 'var(--border-default, rgba(255,255,255,0.09))' }}>·</span>
                    <span>{t('brackets.matchesCount', '{{done}}/{{total}} matches', { done: b.match_stats?.completed || 0, total: b.match_stats?.total || 0 })}</span>
                    {b.match_stats?.live > 0 && (
                      <span style={{ color: 'var(--accent-green, #22c55e)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--accent-green, #22c55e)', animation: 'bracketPulse 1.5s ease-in-out infinite' }} />
                        {t('brackets.liveCount', '{{count}} live', { count: b.match_stats.live })}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em',
                    padding: '3px 10px', borderRadius: 'var(--radius-sm, 6px)',
                    background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
                  }}>
                    {t(`bracketStatus.${b.status}`, b.status)}
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteBracket(b.id); }} style={{
                    background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)',
                    color: 'var(--accent-red, #ef4444)', padding: '0.3rem 0.6rem', borderRadius: 'var(--radius-sm, 6px)', cursor: 'pointer',
                    fontSize: '0.7rem', fontWeight: 600, transition: 'all 0.15s',
                  }}>
                    {t('common.delete', 'Delete')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create bracket modal */}
      <CreateBracketModal isOpen={newCreate} onClose={() => setNewCreate(false)} seasonId={seasonId} teams={teams}
        onCreated={(id) => { setNewCreate(false); fetchAll(); openBracket(id); }} />
    </div>
  );
}

export default Brackets;
